// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The registry layer.
 *
 * THE ONE RULE THAT MATTERS HERE: a request that fails is never reported as a
 * package that does not exist. Those two states look the same to a careless
 * implementation and they are opposites to the user — one means "your
 * assistant invented this", the other means "the wifi dropped". Every path
 * that cannot reach a registry returns `existence: 'error'` carrying the
 * reason, and the interface renders it as an unanswered row with a retry.
 *
 * Endpoint choices, all measured against a third-party Origin rather than
 * assumed:
 *
 * npm — `GET /{name}/latest` is ~2 KB and 404s cleanly for a missing package.
 * The obvious endpoint, `GET /{name}`, is 6.9 MB for react (2.9 MB even with
 * the abbreviated Accept header) because it carries every version ever
 * published; on a phone that is the whole budget spent on one row.
 *
 * PyPI — the PEP 691 simple index at `/simple/{name}/` is currently served as
 * api-version 1.4, which means PEP 700: it carries `versions`, and per file
 * `upload-time`, `size`, `yanked` and a sha256 in `hashes`. So ONE request
 * answers existence, age, release count, yank status and the digests that make
 * the `--require-hashes` artefact real. The `/pypi/{name}/json` endpoint would
 * need a second round trip and is enormous for a package with many releases.
 *
 * pypistats.org sends NO access-control-allow-origin header at all (measured:
 * a 200 with no CORS header). PyPI download counts are therefore unreachable
 * from a browser tab, and ghostdep says so rather than substituting a number
 * from somewhere else.
 */

import type { Ecosystem, RegistryFacts } from './types.ts';

const NPM_REGISTRY = 'https://registry.npmjs.org';
const NPM_API = 'https://api.npmjs.org';
const PYPI = 'https://pypi.org';

/** Below this many weekly downloads, npm's age is worth a second request. */
export const AGE_LOOKUP_THRESHOLD = 50_000;

const TIMEOUT_MS = 15_000;

export interface FetchLike {
  (input: string, init?: RequestInit): Promise<Response>;
}

export interface VerifyDeps {
  readonly fetch: FetchLike;
  readonly signal?: AbortSignal;
}

const EMPTY: RegistryFacts = {
  existence: 'unchecked',
  firstPublished: null,
  latestVersion: null,
  weeklyDownloads: null,
  repository: null,
  releaseCount: null,
  hasInstallScript: null,
  maintainerCount: null,
  yanked: null,
  integrity: null,
  sha256: [],
  error: null,
};

/** A GET that never sends credentials and always has a deadline. */
async function get(deps: VerifyDeps, url: string, accept?: string): Promise<Response> {
  const timer = new AbortController();
  const timeout = setTimeout(() => timer.abort(new Error('timed out')), TIMEOUT_MS);
  const onAbort = () => timer.abort(new Error('cancelled'));
  deps.signal?.addEventListener('abort', onAbort, { once: true });
  try {
    return await deps.fetch(url, {
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      headers: accept ? { Accept: accept } : undefined,
      signal: timer.signal,
    });
  } finally {
    clearTimeout(timeout);
    deps.signal?.removeEventListener('abort', onAbort);
  }
}

function describe(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'the request failed';
}

/** git+ssh://git@github.com/a/b.git and friends → a URL a person can open. */
export function tidyRepository(raw: unknown): string | null {
  const url = typeof raw === 'string' ? raw : typeof (raw as { url?: unknown })?.url === 'string' ? (raw as { url: string }).url : null;
  if (!url) return null;
  const cleaned = url
    .replace(/^git\+/, '')
    .replace(/^git:\/\//, 'https://')
    .replace(/^ssh:\/\/git@/, 'https://')
    .replace(/^git@([^:]+):/, 'https://$1/')
    .replace(/\.git$/, '');
  return /^https?:\/\//.test(cleaned) ? cleaned : null;
}

/* ── npm ──────────────────────────────────────────────────────────────────── */

interface NpmLatest {
  version?: unknown;
  dist?: { integrity?: unknown; tarball?: unknown };
  repository?: unknown;
  maintainers?: unknown;
  scripts?: Record<string, unknown>;
  deprecated?: unknown;
}

const INSTALL_HOOKS = ['preinstall', 'install', 'postinstall'];

export async function npmFacts(deps: VerifyDeps, name: string): Promise<RegistryFacts> {
  let response: Response;
  try {
    response = await get(deps, `${NPM_REGISTRY}/${encodeURIComponent(name).replace(/^%40/, '@')}/latest`);
  } catch (error) {
    return { ...EMPTY, existence: 'error', error: describe(error) };
  }
  if (response.status === 404) return { ...EMPTY, existence: 'missing' };
  if (!response.ok) {
    return { ...EMPTY, existence: 'error', error: `the registry answered ${response.status}` };
  }

  let doc: NpmLatest;
  try {
    doc = (await response.json()) as NpmLatest;
  } catch (error) {
    return { ...EMPTY, existence: 'error', error: describe(error) };
  }

  const scripts = doc.scripts ?? {};
  return {
    ...EMPTY,
    existence: 'exists',
    latestVersion: typeof doc.version === 'string' ? doc.version : null,
    integrity: typeof doc.dist?.integrity === 'string' ? doc.dist.integrity : null,
    repository: tidyRepository(doc.repository),
    maintainerCount: Array.isArray(doc.maintainers) ? doc.maintainers.length : null,
    hasInstallScript: INSTALL_HOOKS.some((hook) => typeof scripts[hook] === 'string'),
  };
}

/**
 * Weekly downloads for many packages at once. The bulk endpoint takes up to 128
 * names and refuses scoped ones, so those go singly — a fact worth encoding
 * rather than discovering when every `@scope/pkg` silently reports null.
 */
export async function npmDownloads(deps: VerifyDeps, names: readonly string[]): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  const plain = names.filter((n) => !n.startsWith('@'));
  const scoped = names.filter((n) => n.startsWith('@'));

  for (let i = 0; i < plain.length; i += 128) {
    const batch = plain.slice(i, i + 128);
    try {
      const response = await get(deps, `${NPM_API}/downloads/point/last-week/${batch.join(',')}`);
      if (!response.ok) continue;
      const doc = (await response.json()) as Record<string, { downloads?: unknown } | null>;
      // A single-name request returns the object directly rather than keyed by
      // name; a one-package paste would otherwise get no counts at all.
      if (batch.length === 1) {
        const single = doc as unknown as { downloads?: unknown; package?: unknown };
        if (typeof single.downloads === 'number') out.set(batch[0], single.downloads);
        continue;
      }
      for (const [name, entry] of Object.entries(doc)) {
        if (entry && typeof entry.downloads === 'number') out.set(name, entry.downloads);
      }
    } catch {
      /* counts are supporting evidence, not the verdict — a failure here leaves
         the column blank rather than failing the row */
    }
  }

  for (const name of scoped) {
    try {
      const response = await get(deps, `${NPM_API}/downloads/point/last-week/${name}`);
      if (!response.ok) continue;
      const doc = (await response.json()) as { downloads?: unknown };
      if (typeof doc.downloads === 'number') out.set(name, doc.downloads);
    } catch {
      /* as above */
    }
  }
  return out;
}

/**
 * First publication date. This needs the full packument, which is megabytes for
 * a popular package — so it is only ever called for packages that are NOT
 * popular, which is both the cheap case and the only case where the answer
 * changes anything. A brand-new squat's packument is a couple of kilobytes.
 */
export async function npmFirstPublished(deps: VerifyDeps, name: string): Promise<{ created: string | null; releases: number | null }> {
  try {
    const response = await get(deps, `${NPM_REGISTRY}/${encodeURIComponent(name).replace(/^%40/, '@')}`);
    if (!response.ok) return { created: null, releases: null };
    const doc = (await response.json()) as { time?: Record<string, string>; versions?: Record<string, unknown> };
    const created = typeof doc.time?.created === 'string' ? doc.time.created : null;
    const releases = doc.versions ? Object.keys(doc.versions).length : null;
    return { created, releases };
  } catch {
    return { created: null, releases: null };
  }
}

/* ── PyPI ─────────────────────────────────────────────────────────────────── */

interface SimpleFile {
  filename?: unknown;
  hashes?: Record<string, unknown>;
  'upload-time'?: unknown;
  yanked?: unknown;
}

export async function pypiFacts(deps: VerifyDeps, name: string): Promise<RegistryFacts> {
  let response: Response;
  try {
    response = await get(deps, `${PYPI}/simple/${encodeURIComponent(name)}/`, 'application/vnd.pypi.simple.v1+json');
  } catch (error) {
    return { ...EMPTY, existence: 'error', error: describe(error) };
  }
  if (response.status === 404) return { ...EMPTY, existence: 'missing' };
  if (!response.ok) return { ...EMPTY, existence: 'error', error: `PyPI answered ${response.status}` };

  let doc: { files?: SimpleFile[]; versions?: unknown[]; meta?: { 'api-version'?: unknown } };
  try {
    doc = await response.json();
  } catch (error) {
    return { ...EMPTY, existence: 'error', error: describe(error) };
  }

  const files = Array.isArray(doc.files) ? doc.files : [];
  const versions = Array.isArray(doc.versions) ? doc.versions : [];

  let earliest: string | null = null;
  for (const file of files) {
    const uploaded = typeof file['upload-time'] === 'string' ? file['upload-time'] : null;
    if (uploaded && (earliest === null || uploaded < earliest)) earliest = uploaded;
  }

  // Digests for the pin. The latest version's files only — pinning one version
  // and listing every historical file's hash would produce a requirements.txt
  // that pip rejects.
  const latest = versions.length ? String(versions[versions.length - 1]) : null;
  const sha256: { filename: string; sha256: string }[] = [];
  let anyYanked = false;
  for (const file of files) {
    const filename = typeof file.filename === 'string' ? file.filename : null;
    const digest = typeof file.hashes?.sha256 === 'string' ? (file.hashes.sha256 as string) : null;
    if (!filename || !digest) continue;
    if (latest && !filenameMatchesVersion(filename, name, latest)) continue;
    if (file.yanked) {
      anyYanked = true;
      continue;
    }
    sha256.push({ filename, sha256: digest });
  }

  return {
    ...EMPTY,
    existence: 'exists',
    latestVersion: latest,
    firstPublished: earliest,
    releaseCount: versions.length || null,
    yanked: anyYanked,
    sha256,
    // Stated as null rather than guessed. pypistats.org has no CORS header, so
    // this number cannot be obtained from a browser at all.
    weeklyDownloads: null,
  };
}

/**
 * Does this filename belong to this version? PyPI normalises the project name
 * in wheel filenames (PEP 427: runs of `-`, `_` and `.` become `_`), so a
 * literal `${name}-${version}` prefix test fails on exactly the packages whose
 * names are punctuated — `ruamel.yaml`, `zope.interface`, `backports.zoneinfo`.
 */
export function filenameMatchesVersion(filename: string, name: string, version: string): boolean {
  const quote = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Split on separator runs and escape each PIECE, then join with the class.
  // Building the class first and escaping afterwards escapes the brackets and
  // the `+` that were just inserted, and the pattern then matches nothing —
  // which reads as "this project publishes no files" for every punctuated
  // name on PyPI.
  const normalised = name.split(/[-_.]+/).map(quote).join('[-_.]+');
  return new RegExp(`^${normalised}-${quote(version)}(?:[-.]|$)`, 'i').test(filename);
}

/* ── the pool ─────────────────────────────────────────────────────────────── */

export interface Progress {
  (done: number, total: number, name: string): void;
}

/**
 * Look up every candidate, at a bounded concurrency. Bounded because twenty-two
 * simultaneous requests to one host is worse for the user than six: browsers
 * queue per-origin anyway, and a burst is what a registry rate-limiter is
 * looking for.
 */
export async function verifyAll(
  deps: VerifyDeps,
  candidates: readonly { ecosystem: Ecosystem; name: string }[],
  onProgress?: Progress,
  concurrency = 6,
): Promise<Map<string, RegistryFacts>> {
  const results = new Map<string, RegistryFacts>();
  const key = (c: { ecosystem: Ecosystem; name: string }) => `${c.ecosystem}:${c.name}`;
  let done = 0;
  let cursor = 0;

  const workers = Array.from({ length: Math.min(concurrency, candidates.length) }, async () => {
    for (;;) {
      const index = cursor++;
      if (index >= candidates.length) return;
      const candidate = candidates[index];
      const facts =
        candidate.ecosystem === 'npm'
          ? await npmFacts(deps, candidate.name)
          : await pypiFacts(deps, candidate.name);
      results.set(key(candidate), facts);
      done++;
      onProgress?.(done, candidates.length, candidate.name);
    }
  });
  await Promise.all(workers);

  // npm downloads in bulk, then ages for the ones where age is the question.
  const npmNames = candidates
    .filter((c) => c.ecosystem === 'npm' && results.get(key(c))?.existence === 'exists')
    .map((c) => c.name);
  if (npmNames.length) {
    const counts = await npmDownloads(deps, npmNames);
    for (const name of npmNames) {
      const existing = results.get(`npm:${name}`);
      if (!existing) continue;
      const weekly = counts.get(name) ?? null;
      results.set(`npm:${name}`, { ...existing, weeklyDownloads: weekly });
    }

    const needAge = npmNames.filter((n) => (counts.get(n) ?? 0) < AGE_LOOKUP_THRESHOLD);
    let ageCursor = 0;
    const ageWorkers = Array.from({ length: Math.min(concurrency, needAge.length) }, async () => {
      for (;;) {
        const index = ageCursor++;
        if (index >= needAge.length) return;
        const name = needAge[index];
        const { created, releases } = await npmFirstPublished(deps, name);
        const existing = results.get(`npm:${name}`);
        if (existing) {
          results.set(`npm:${name}`, { ...existing, firstPublished: created, releaseCount: releases });
        }
      }
    });
    await Promise.all(ageWorkers);
  }

  return results;
}
